/* Website Development service page — two-column hero, rich sections.
   Route: /website-development. */
import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ArrowDown,
  ArrowUp,
  Check,
  Sparkles,
  MapPin,
  Target,
  Zap,
  Search,
  DollarSign,
  Send,
  CheckCircle,
} from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TRUST_LINES = [
  "Delivered on time, every time",
  "Mobile-first, SEO-ready from day one",
  "US-based project management with direct communication",
];

const STATS = [
  { value: "50+", label: "Websites Delivered" },
  { value: "98%", label: "Client Satisfaction" },
  { value: "5-Day", label: "Express Delivery Available" },
];

const PLANS = [
  {
    name: "Starter",
    price: "$499",
    idealFor: "Local businesses and startups needing an online presence fast",
    features: [
      "Single page or landing page",
      "Mobile responsive and cross-browser compatible",
      "Contact form with email notification",
      "Basic on-page SEO (title tags, meta descriptions, image alt text)",
      "Google Analytics and Search Console setup",
      "2 rounds of revisions",
      "Delivered in 5 business days",
    ],
    popular: false,
  },
  {
    name: "Growth",
    price: "$999",
    idealFor: "Service businesses ready to scale their digital presence",
    features: [
      "Up to 5 custom-designed pages",
      "Full UI/UX design with brand colour integration",
      "CMS integration for easy content updates",
      "Advanced lead capture forms with CRM-ready export",
      "Page speed and Core Web Vitals optimisation",
      "Blog or news section setup",
      "Google Analytics 4 with conversion tracking",
      "3 rounds of revisions",
      "Delivered in 10 business days",
    ],
    popular: true,
  },
  {
    name: "Premium",
    price: "$1,500",
    idealFor: "Established brands and businesses targeting serious growth",
    features: [
      "Up to 10 fully custom-designed pages",
      "Premium brand identity integration (colours, fonts, tone)",
      "Payment gateway or booking system integration",
      "Advanced SEO setup with keyword research and schema markup",
      "Blog with category and tag system",
      "Social media feed integration",
      "Priority support for 30 days post-launch",
      "Unlimited revisions during development",
      "Delivered in 15 business days",
    ],
    popular: false,
  },
];

const STEPS = [
  { n: 1, title: "Submit Your Inquiry", desc: "Fill out the form and tell us your goals" },
  { n: 2, title: "Free Strategy Call", desc: "We discuss your vision and confirm the scope" },
  { n: 3, title: "Design and Build", desc: "Our team gets to work immediately" },
  { n: 4, title: "Review and Launch", desc: "You approve, we go live" },
];

const WHY_US = [
  { icon: Sparkles, title: "AI-Powered Design Process", desc: "We use cutting-edge AI tools to design faster and smarter" },
  { icon: MapPin, title: "US-Based Project Management", desc: "Direct communication, no middlemen, no delays" },
  { icon: Target, title: "Built for Lead Generation", desc: "Every page is designed with conversion as the primary goal" },
  { icon: Zap, title: "Fast Turnaround", desc: "Most projects go live within 5 to 15 business days" },
  { icon: Search, title: "SEO-Ready From Day One", desc: "Your site is built to rank, not just to look good" },
  { icon: DollarSign, title: "Transparent Pricing", desc: "What you see is what you pay. No surprises, ever." },
];

const GOALS = ["New Website", "Redesign"];
const BUDGETS = ["$499", "$999", "$1,500", "Custom Budget"];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const WebsiteDevelopmentPage = () => {
  const [form, setForm] = useState({
    full_name: "",
    business_name: "",
    email: "",
    phone: "",
    website_goal: "",
    budget: "",
    description: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const res = await fetch(`${API_URL}/api/website-inquiry`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setSubmitted(true);
    } catch (err) {
      setError("Something went wrong sending your inquiry. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-slate-900/70 border border-slate-700 rounded-lg px-4 py-3 text-sm text-slate-100 placeholder-slate-500 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none transition-colors";

  const inquiryForm = (
    <div
      data-testid="webdev-inquiry-card"
      className="relative rounded-2xl border border-blue-500/40 bg-slate-800/50 backdrop-blur-xl p-6 sm:p-8 shadow-[0_0_70px_-15px_rgba(59,130,246,0.4)] text-left"
    >
      <h2 className="font-['Poppins'] text-2xl font-bold tracking-tight">
        Request a Free Consultation
      </h2>
      <p className="mt-2 text-sm text-slate-400">
        Tell us about your project — we reply within 24 hours.
      </p>

      {submitted ? (
        <div
          className="mt-6 rounded-xl border border-emerald-500/40 bg-emerald-500/10 p-5 flex items-start gap-3"
          data-testid="webdev-form-success"
        >
          <CheckCircle className="w-6 h-6 text-emerald-400 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-emerald-300">Thank you!</p>
            <p className="mt-1 text-sm text-emerald-200/80 leading-relaxed">
              We will be in touch within 24 hours.
            </p>
          </div>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="mt-6 space-y-4" data-testid="webdev-inquiry-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Full Name *</label>
              <input required value={form.full_name} onChange={set("full_name")} placeholder="Jane Cooper" className={inputCls} data-testid="webdev-input-name" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Business Name</label>
              <input value={form.business_name} onChange={set("business_name")} placeholder="Acme Services LLC" className={inputCls} data-testid="webdev-input-business" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Email Address *</label>
              <input required type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} data-testid="webdev-input-email" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Phone Number</label>
              <input type="tel" value={form.phone} onChange={set("phone")} placeholder="+1 (555) 000-0000" className={inputCls} data-testid="webdev-input-phone" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Website Goal *</label>
              <select required value={form.website_goal} onChange={set("website_goal")} className={`${inputCls} appearance-none`} data-testid="webdev-select-goal">
                <option value="" disabled>Select a goal…</option>
                {GOALS.map((g) => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-300 mb-1.5">Budget *</label>
              <select required value={form.budget} onChange={set("budget")} className={`${inputCls} appearance-none`} data-testid="webdev-select-budget">
                <option value="" disabled>Select a budget…</option>
                {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-300 mb-1.5">Project Description</label>
            <textarea
              rows={4}
              value={form.description}
              onChange={set("description")}
              placeholder="Tell us about your business, your customers, and what the website needs to achieve…"
              className={`${inputCls} resize-none`}
              data-testid="webdev-input-description"
            />
          </div>

          {error && (
            <p className="text-sm text-red-400" data-testid="webdev-form-error">{error}</p>
          )}

          <button
            type="submit"
            disabled={loading}
            data-testid="webdev-submit-btn"
            className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 disabled:opacity-60 text-white font-semibold px-8 py-3.5 rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
          >
            <Send className="w-4 h-4" />
            {loading ? "Sending…" : "Send My Request"}
          </button>
        </form>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 font-['Inter',-apple-system,sans-serif] antialiased">
      <Header />

      {/* ── Hero — two columns: pitch (left) + inquiry form (right) ── */}
      <section id="webdev-hero" className="relative overflow-hidden pt-28 pb-16 sm:pt-36 sm:pb-20">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-slate-950 to-black" />
          <motion.div
            className="absolute -top-32 right-[-8rem] w-[30rem] h-[30rem] rounded-full bg-blue-600/20 blur-3xl"
            animate={{ x: [0, -40, 0], y: [0, 50, 0] }}
            transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div
            className="absolute bottom-[-10rem] left-[-6rem] w-[26rem] h-[26rem] rounded-full bg-indigo-600/15 blur-3xl"
            animate={{ x: [0, 50, 0], y: [0, -30, 0] }}
            transition={{ duration: 19, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start text-left">
          {/* Left column */}
          <div>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={0}
              className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400"
            >
              Website Development Services
            </motion.p>
            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
              className="mt-4 font-['Poppins'] text-4xl sm:text-5xl font-bold tracking-tight leading-[1.08]"
            >
              We Build Websites That Turn Visitors Into{" "}
              <span className="bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                Paying Customers
              </span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
              className="mt-6 text-base sm:text-lg text-slate-400 leading-relaxed"
            >
              At Digital Master Media LLC, we design and develop high-performance,
              custom websites tailored to your business goals. Whether you are a
              local service provider, a growing startup, or an established brand,
              we deliver websites that look premium, load fast, and generate real leads.
            </motion.p>

            <motion.ul variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-7 space-y-3">
              {TRUST_LINES.map((line) => (
                <li key={line} className="flex items-start gap-3 text-sm sm:text-[15px] text-slate-300">
                  <span className="mt-0.5 w-5 h-5 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center flex-shrink-0">
                    <Check className="w-3 h-3 text-emerald-400" strokeWidth={3} />
                  </span>
                  {line}
                </li>
              ))}
            </motion.ul>

            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-9">
              <button
                onClick={() => scrollTo("plans")}
                data-testid="webdev-view-packages-btn"
                className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
              >
                View Our Packages
                <ArrowDown className="w-4 h-4" />
              </button>
            </motion.div>
          </div>

          {/* Right column — always-visible inquiry form */}
          <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={2}>
            {inquiryForm}
          </motion.div>
        </div>
      </section>

      {/* ── Social proof strip ────────────────────────────────── */}
      <section className="border-y border-slate-800/70 bg-slate-900/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 sm:grid-cols-3 gap-8" data-testid="webdev-stats-strip">
          {STATS.map((s) => (
            <div key={s.label} className="text-left">
              <p className="font-['Poppins'] text-3xl sm:text-4xl font-bold tracking-tight bg-gradient-to-r from-blue-400 to-indigo-400 bg-clip-text text-transparent">
                {s.value}
              </p>
              <p className="mt-1 text-sm font-medium text-slate-400">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Pricing Plans (bento, glassmorphism) ─────────────── */}
      <section id="plans" className="py-16 sm:py-24 scroll-mt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
            Pricing
          </p>
          <h2 className="mt-3 font-['Poppins'] text-3xl sm:text-4xl font-bold tracking-tight">
            Simple, Transparent Pricing. No Hidden Fees.
          </h2>
          <p className="mt-4 text-slate-400 max-w-2xl leading-relaxed">
            Choose the plan that fits your business. Every plan includes a dedicated
            project manager, mobile responsive design, and post-launch testing.
          </p>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 items-stretch" data-testid="webdev-plans-grid">
            {PLANS.map((plan, i) => (
              <motion.div
                key={plan.name}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                custom={i}
                data-testid={`webdev-plan-${plan.name.toLowerCase()}`}
                className={`relative flex flex-col rounded-2xl p-8 backdrop-blur-xl transition-all duration-300 text-left ${
                  plan.popular
                    ? "bg-slate-800/60 border-2 border-blue-500/60 shadow-[0_0_60px_-12px_rgba(59,130,246,0.45)] md:-translate-y-2"
                    : "bg-slate-800/35 border border-slate-700/60 hover:border-slate-600"
                }`}
              >
                {plan.popular && (
                  <span
                    data-testid="webdev-most-popular-badge"
                    className="absolute -top-3.5 left-8 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 px-3.5 py-1 text-[11px] font-bold uppercase tracking-wider text-white shadow-lg shadow-blue-600/30"
                  >
                    Most Popular
                  </span>
                )}
                <h3 className="font-['Poppins'] text-xl font-semibold">{plan.name}</h3>
                <p className="mt-3 flex items-baseline gap-2">
                  <span className="font-['Poppins'] text-4xl font-bold tracking-tight">{plan.price}</span>
                  <span className="text-sm text-slate-500">one-time</span>
                </p>
                <p className="mt-4 text-[13px] text-slate-400 leading-relaxed border-l-2 border-blue-500/40 pl-3">
                  <span className="font-semibold text-slate-300">Ideal for:</span> {plan.idealFor}
                </p>
                <ul className="mt-6 space-y-3 flex-1">
                  {plan.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-sm text-slate-300 leading-snug">
                      <Check className={`w-4 h-4 mt-0.5 flex-shrink-0 ${plan.popular ? "text-blue-400" : "text-emerald-400"}`} strokeWidth={2.5} />
                      {f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => {
                    setForm((f) => ({ ...f, budget: plan.price }));
                    scrollTo("webdev-hero");
                  }}
                  data-testid={`webdev-plan-${plan.name.toLowerCase()}-cta`}
                  className={`mt-8 w-full py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                    plan.popular
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white shadow-md shadow-blue-600/25 hover:-translate-y-0.5"
                      : "border border-slate-600 text-slate-200 hover:border-blue-500 hover:text-white hover:bg-blue-600/10"
                  }`}
                >
                  Start with {plan.name}
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────── */}
      <section className="py-16 sm:py-20 border-t border-slate-800/70 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
            Process
          </p>
          <h2 className="mt-3 font-['Poppins'] text-3xl sm:text-4xl font-bold tracking-tight">
            From Brief to Live in Days, Not Months
          </h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-testid="webdev-steps-grid">
            {STEPS.map((s, i) => (
              <motion.div
                key={s.n}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                custom={i}
                className="relative rounded-2xl border border-slate-700/60 bg-slate-800/35 backdrop-blur-xl p-6 hover:border-blue-500/50 hover:-translate-y-1 transition-all duration-300"
                data-testid={`webdev-step-${s.n}`}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-600/25 font-['Poppins'] font-bold text-white text-lg">
                  {s.n}
                </div>
                <h3 className="mt-5 font-semibold text-[15px] leading-snug">{s.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{s.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Choose Us — 3×2 bento ────────────────────────── */}
      <section className="py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <p className="text-xs font-semibold uppercase tracking-[0.22em] text-blue-400">
            Why Choose Us
          </p>
          <h2 className="mt-3 font-['Poppins'] text-3xl sm:text-4xl font-bold tracking-tight">
            Built for Businesses That Mean Business
          </h2>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="webdev-why-grid">
            {WHY_US.map((w, i) => (
              <motion.div
                key={w.title}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                custom={i}
                className="rounded-2xl border border-slate-700/60 bg-slate-800/35 backdrop-blur-xl p-6 hover:border-blue-500/50 hover:-translate-y-1 transition-all duration-300"
                data-testid={`webdev-why-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center shadow-md shadow-blue-600/25">
                  <w.icon className="w-5 h-5 text-white" strokeWidth={2.25} />
                </div>
                <h3 className="mt-5 font-semibold text-[15px] leading-snug">{w.title}</h3>
                <p className="mt-2 text-sm text-slate-400 leading-relaxed">{w.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Bottom CTA ───────────────────────────────────────── */}
      <section className="py-16 sm:py-24 border-t border-slate-800/70 bg-slate-900/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-left">
          <h2 className="font-['Poppins'] text-3xl sm:text-4xl font-bold tracking-tight max-w-2xl">
            Ready to Build Something Great?
          </h2>
          <p className="mt-4 text-slate-400 max-w-xl leading-relaxed">
            Join the businesses that trust Digital Master Media LLC to build their
            digital foundation.
          </p>
          <button
            onClick={() => scrollTo("webdev-hero")}
            data-testid="webdev-bottom-cta"
            className="mt-9 inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold px-8 py-4 rounded-xl shadow-lg shadow-blue-600/25 hover:shadow-blue-500/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-200"
          >
            Start Your Project Today
            <ArrowUp className="w-4 h-4" />
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default WebsiteDevelopmentPage;
